import { QueryKey, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query'

import { useQueryKeyBuilder } from 'data/hooks/_helpers'
import { addToStackCache, invalidateStacks } from 'data/hooks/stacks/stackOperations'
import { fetchAndReturn } from 'data/utils/utils'
import { AppTemplate } from 'features/templates/types'

const LIST_NAME = 'useAppTemplates'
const ENDPOINT = 'app-templates/'
const INSTALL_ENDPOINT = 'install/'
const EXPORT_ENDPOINT = 'export/'

type AppTemplatesDto = AppTemplate[]

function useQueryKey() {
    return useQueryKeyBuilder([LIST_NAME], { includeAuthKeys: true, includeStackId: true })
}

type QueryOptions = UseQueryOptions<AppTemplatesDto, unknown, AppTemplatesDto, QueryKey>

export function useAppTemplates(options: QueryOptions = {}) {
    const queryKey = useQueryKey()
    const queryResult = useQuery<AppTemplatesDto>(
        queryKey,
        async () => {
            const results = (await fetchAndReturn(ENDPOINT)) as AppTemplatesDto
            return results
        },
        options
    )

    return queryResult
}

export type InstallAppTemplatePayload = {
    templateId: string
    stackOptions?: Partial<StackDto['options']>
}

export type InstallAppTemplateResponse = {
    stack: StackDto
}

export function useInstallAppTemplate(
    options: UseMutationOptions<InstallAppTemplateResponse, unknown, InstallAppTemplatePayload> = {}
) {
    return useMutation(
        async (payload: InstallAppTemplatePayload) => {
            return fetchAndReturn(`${ENDPOINT}${payload.templateId}/${INSTALL_ENDPOINT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    stack_options: payload.stackOptions,
                }),
            }) as Promise<InstallAppTemplateResponse>
        },
        {
            ...options,
            onSuccess: (data, variables, context) => {
                options.onSuccess?.(data, variables, context)
                addToStackCache(data.stack)
            },
            onSettled: () => {
                invalidateStacks()
            },
        }
    )
}

export type ExportAppTemplatePayload = {
    template: Omit<AppTemplate, 'id' | 'version' | 'bundle_data'>
    stackSid: string
}

export type ExportAppTemplateResponse = AppTemplate

export function useExportAppTemplate(
    options: UseMutationOptions<ExportAppTemplateResponse, unknown, ExportAppTemplatePayload> = {}
) {
    return useMutation(async (payload: ExportAppTemplatePayload) => {
        return fetchAndReturn(`${ENDPOINT}${EXPORT_ENDPOINT}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                stack_id: payload.stackSid,
                template: payload.template,
            }),
        }) as Promise<ExportAppTemplateResponse>
    }, options)
}
